package com.dynamicruntime.common.schema

/**
 * A parsed field of an object [SchType]. [valueType] is the resolved schema for
 * the field's value — either an inline schema or, for a `$ref` field, the bound
 * target type. It is populated during parsing: inline fields immediately, `$ref`
 * fields in the reference-resolution pass once all types are parsed.
 */
class SchProperty(
    val name: String,
    val description: String?,
    /** Fully-qualified target type name if this field is a `$ref`, else null. */
    val refName: String?,
    /**
     * The **field's** own `title` -- a short human label, against [description]'s longer explanation; null when
     * none is declared. On the property, not read off [valueType], because a `$ref` field's [valueType] is the
     * *shared* target instance: a title from there would label every field referencing that type identically.
     */
    val title: String? = null,
    /**
     * `g-optionalContents`: this field's object value is a **fragment**, so the validator checks its fields but
     * not its completeness -- neither [SchType.required] nor a conditional's requiredness (issue #487). On the
     * property, not [valueType], because a `$ref` field's value type is shared -- only this use of it is a
     * fragment. See [SCH.optionalContents].
     */
    val optionalContents: Boolean = false,
    /**
     * `g-presentation` declared **at this use site** (issue #540): a display hint, advisory only. On the
     * property, not read off [valueType], for the same reason as [title] -- a `$ref` field's [valueType] is the
     * *shared* target instance, so a hint written beside the `$ref` would otherwise be lost, and two fields
     * referencing one type could not be hinted differently. Null falls back to [valueType]'s own hint.
     */
    val presentation: String? = null,
    /**
     * `g-visibleWhen`: a cfact expression deciding whether this field is shown, **evaluated on the frontend**
     * (issue #564) against the caller's delivered cfacts; null when the field is always shown. On the property,
     * not [valueType], for the same reason as [title] and [presentation] -- a `$ref` field's value type is
     * shared, so a gate written beside the `$ref` belongs to this use site. The served schema keeps the field; on
     * trait data the backend enforces the gate on every write (issue #830), and on an endpoint input the handler
     * does. See [SCH.visibleWhen].
     */
    val visibleWhen: String? = null,
    /**
     * `g-sensitive`: the value is a secret the request log redacts (issue #1168). On the property, for the same reason
     * as [visibleWhen]. See [SCH.sensitive].
     */
    val sensitive: Boolean = false,
) {
    /** Resolved value schema. Set once during parsing (see class doc). */
    lateinit var valueType: SchType
}
